import React, { useMemo, useState } from 'react';
import { Button, CardHeader, Flex, ListItem, Text } from '@/components/ui';
import { IconDownload, IconEye } from '@tabler/icons-react';
import { FormTreeNode } from './utils/formTreeUtils';
import {
  downloadCsvFile,
  generateFormDemoCsv,
  generateFormTemplateCsv,
} from '../utils/csvTemplateGenerator';
import { StudyFormHoverCard } from '@/components/data-import/StudyFormHoverCard';
import { CsvPreviewModal } from './CsvPreviewModal';
import { FormIcon } from './FormIcon';
import { getFormDisplayName } from '@/utils/formUtils';
import type { StudyForm } from '@/types/studyForms';
import type { DemoDataSettings } from '../types/demoDataSettings';
import './FormSelector/FormSelector.css';

interface FormTreeWithTemplatesProps {
  tree: FormTreeNode[];
  allForms?: StudyForm[];
  previewWithDemo?: boolean; // Whether preview should show demo data by default
  onPreviewWithDemoChange?: (enabled: boolean) => void; // Callback for toggle change
  onDownloadAll?: (type: 'template' | 'demo') => void;
  demoDataSettings?: DemoDataSettings; // Demo data generation settings
  onDemoDataSettingsChange?: (settings: DemoDataSettings) => void; // Callback for settings change
}

export const FormTreeWithTemplates: React.FC<FormTreeWithTemplatesProps> = ({
  tree,
  allForms = [],
  previewWithDemo = false,
  onPreviewWithDemoChange,
  onDownloadAll,
  demoDataSettings,
  onDemoDataSettingsChange,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewForm, setPreviewForm] = useState<FormTreeNode | null>(null);
  const [previewType, setPreviewType] = useState<'template' | 'demo'>(
    'template'
  );

  // Convert allForms array to Map for StudyFormHoverCard
  const formsMap = useMemo(() => {
    const map = new Map<number, StudyForm>();
    allForms.forEach(form => {
      map.set(form.id, form);
    });
    return map;
  }, [allForms]);

  const handleDownloadTemplate = (form: FormTreeNode) => {
    // Use parent form ID directly from tree
    // The tree structure shows which parent this form belongs to in the current context
    const parentFormId = form.parent?.id;

    const csv = generateFormTemplateCsv(form, parentFormId);
    const parentName = form.parent?.name;
    const filename = parentName
      ? `${form.name} (${parentName})_template.csv`
      : `${form.name}_template.csv`;
    downloadCsvFile(csv, filename);
  };

  const handleDownloadDemo = (form: FormTreeNode) => {
    // Use parent form ID directly from tree
    // The tree structure shows which parent this form belongs to in the current context
    const parentFormId = form.parent?.id;

    const csv = generateFormDemoCsv(form, parentFormId, demoDataSettings);
    const parentName = form.parent?.name;
    const filename = parentName
      ? `${form.name} (${parentName})_demo.csv`
      : `${form.name}_demo.csv`;
    downloadCsvFile(csv, filename);
  };

  const handlePreview = (form: FormTreeNode, contextParent?: FormTreeNode) => {
    // Create a copy of the form with the correct parent for this context
    // This ensures forms with multiple parents show the correct parent ID in preview
    const formWithContext = contextParent
      ? { ...form, parent: contextParent }
      : form;
    setPreviewForm(formWithContext);
    // Use previewWithDemo prop to determine preview type
    setPreviewType(previewWithDemo ? 'demo' : 'template');
    setPreviewModalOpen(true);
  };

  const handlePreviewDownload = (type: 'template' | 'demo') => {
    if (!previewForm) return;

    if (type === 'template') {
      handleDownloadTemplate(previewForm);
    } else {
      handleDownloadDemo(previewForm);
    }
    setPreviewModalOpen(false);
  };

  const renderFormNode = (
    node: FormTreeNode,
    level: number = 0,
    contextParent?: FormTreeNode
  ): React.ReactNode => {
    const indentStyle = {
      '--tree-level': level || 0,
      flex: 1,
      cursor: 'pointer',
    } as React.CSSProperties;

    const displayName = getFormDisplayName(node);

    return (
      <React.Fragment key={node.id}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <StudyFormHoverCard
            form={node}
            fieldCount={node.fieldCount}
            allForms={formsMap}
          >
            <ListItem
              style={indentStyle}
              // style={{ cursor: 'pointer', flex: 1 }}
              className="form-selector-tree-item"
              startIcon={
                <>
                  {level > 0 && (
                    <div className="form-selector-tree-indicator" />
                  )}
                  <FormIcon form={node} availableForms={allForms} />
                </>
              }
              primary={
                <Flex align="center" gap="2" style={{ minWidth: 0 }}>
                  <Text
                    weight="medium"
                    size="2xsmall"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {node.name}
                  </Text>
                  <Text
                    size="3xsmall"
                    color="gray"
                    className="form-selector-subform-display-name"
                    style={{ flexShrink: 0 }}
                  >
                    [{node.fieldCount || 0} fields]
                  </Text>
                </Flex>
              }
              secondary={
                <Text
                  size="3xsmall"
                  color="primary"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayName}
                </Text>
              }
            />
          </StudyFormHoverCard>

          <Flex align="center" gap="2" style={{ flexShrink: 0 }}>
            <Button
              size="xsmall"
              variant="outlined"
              startIcon={<IconEye size={16} />}
              onClick={() => handlePreview(node, contextParent)}
            >
              Preview
            </Button>
            <Button
              size="xsmall"
              variant="outlined"
              startIcon={<IconDownload size={16} />}
              onClick={() => {
                // Use context parent for download if available
                const parentFormId = contextParent?.id || node.parent?.id;
                const csv = generateFormTemplateCsv(node, parentFormId);
                const parentName = contextParent?.name || node.parent?.name;
                const filename = parentName
                  ? `${node.name} (${parentName})_template.csv`
                  : `${node.name}_template.csv`;
                downloadCsvFile(csv, filename);
              }}
            >
              Template
            </Button>
            <Button
              size="xsmall"
              variant="outlined"
              startIcon={<IconDownload size={16} />}
              onClick={() => {
                // Use context parent for download if available
                const parentFormId = contextParent?.id || node.parent?.id;
                const csv = generateFormDemoCsv(node, parentFormId, demoDataSettings);
                const parentName = contextParent?.name || node.parent?.name;
                const filename = parentName
                  ? `${node.name} (${parentName})_demo.csv`
                  : `${node.name}_demo.csv`;
                downloadCsvFile(csv, filename);
              }}
            >
              Demo ({demoDataSettings?.numberOfRows || 25} rows)
            </Button>
          </Flex>
        </div>

        {/* Render children recursively */}
        {node.children.length > 0 && (
          <div className="form-selector-tree-children-group">
            {node.children.map(child => renderFormNode(child, level + 1, node))}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      <Flex direction="column" gap="4">
        {/* Header with bulk download buttons */}
        <Flex align="center" justify="between">
          <CardHeader cardTitle="Study Forms Templates"></CardHeader>
          {onDownloadAll && (
            <Flex gap="2">
              <Button
                variant="outlined"
                startIcon={<IconDownload />}
                onClick={() => onDownloadAll('template')}
              >
                Download All Templates
              </Button>
              <Button
                variant="outlined"
                startIcon={<IconDownload />}
                onClick={() => onDownloadAll('demo')}
              >
                Download All Demos
              </Button>
            </Flex>
          )}
        </Flex>

        {/* Forms tree */}
        <div>
          {tree.length === 0 ? (
            <Text size="2" color="gray" align="center" py="4">
              No forms available
            </Text>
          ) : (
            <div>{tree.map(node => renderFormNode(node, 0))}</div>
          )}
        </div>
      </Flex>

      {/* CSV Preview Modal */}
      {previewForm && (
        <CsvPreviewModal
          form={previewForm}
          type={previewType}
          open={previewModalOpen}
          onOpenChange={setPreviewModalOpen}
          onDownload={handlePreviewDownload}
          previewWithDemo={previewWithDemo}
          onPreviewWithDemoChange={onPreviewWithDemoChange}
          allForms={allForms}
          demoDataSettings={demoDataSettings}
          onDemoDataSettingsChange={onDemoDataSettingsChange}
        />
      )}
    </>
  );
};
